/**
 * GlobalJobsPill v2 — cleaner Linear-style floating widget.
 *
 * Improvements over v1 (per user feedback "worst possible UI"):
 *   • White card with subtle elevation instead of slate-on-slate
 *   • Larger hit area for the toggle, clearer affordance
 *   • Block titles shown when available (from params.label fallback to kind)
 *   • Per-row progress bar with gradient — visually richer
 *   • Stop button per job (admin/owner only, hits /jobs/:id/cancel)
 *   • Auto-hides when empty
 */
import React, { useState, useMemo, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Loader2, ChevronDown, ChevronUp, X, Sparkles, RotateCcw, AlertCircle, Minus } from 'lucide-react';
import { useJobs } from '../context/JobsContext';
import useIsInFocusedFlow from '../hooks/useIsInFocusedFlow';
import api from '../api/client';
import { toast } from 'sonner';

const KIND_LABEL = {
  ai_quiz: 'Quiz',
  ai_lab: 'Lab',
  ai_assignment: 'Assignment',
  ai_reading: 'Reading',
  ai_richtext: 'Reading Material',
  ai_ppt: 'PPT',
  ai_liveclassppt: 'Live Class PPT',
  ai_video: 'Video',
  ai_block_generation: 'Block',
  gamma_ppt: 'PPT',
  video_generation: 'Video',
  batch_generation: 'Batch',
  smoke_test: 'Smoke test',
};

export default function GlobalJobsPill() {
  const { rows, runningCount, refresh } = useJobs();
  // Visibility rule (Feb 2026): show the pill whenever the current user
  // actually has running jobs or a recently-failed batch — regardless
  // of org feature flags. The previous gating on
  // `ENABLE_PARALLEL_GENERATION` was a hold-over from the Acme pilot
  // and meant that SuperAdmins (whose home org is the platform org,
  // where the flag is off) never saw any progress when they kicked off
  // a batch on a tenant course. Since `useJobs` already scopes rows to
  // the authenticated user, there's no information-leak risk; orgs
  // that don't run parallel batches will simply never have rows to
  // show, so the auto-hide at line 82 keeps the UI clean for them.
  const [expanded, setExpanded] = useState(false);
  // Auto-collapse on route change. Without this, the user opens the
  // pill on /courses/X/edit, navigates to /analytics, and the pill
  // stays expanded over the new page — covering content and looking
  // sticky. Collapsing on `pathname` change keeps the pill on the
  // screen (still useful for progress at a glance) but folds it down
  // to the compact summary state so it never obscures the new page.
  // Feb 28 2026 — user report.
  const location = useLocation();
  useEffect(() => {
    setExpanded(false);
  }, [location.pathname]);
  // Auto-collapse when any dialog/modal opens. Dialogs from
  // shadcn/Radix dispatch a `data-state="open"` attribute on
  // `<body>` (via the Portal's `data-radix-popper-content-wrapper`
  // sibling) — we listen for that and fold the pill so it doesn't
  // peek through the modal's z-stack.
  useEffect(() => {
    const observer = new MutationObserver(() => {
      const hasOpenDialog = document.querySelector(
        '[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"]',
      );
      if (hasOpenDialog) setExpanded(false);
    });
    observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-state'] });
    return () => observer.disconnect();
  }, []);
  // Track which batch parents the user has dismissed so we don't keep
  // nagging them with the same "X failed" row after they've chosen to
  // ignore it. Per-session memory (intentionally not persisted —
  // dismissal should reset on reload so the user re-confirms intent).
  const [dismissedFailures, setDismissedFailures] = useState(new Set());
  // Minimize-to-badge mode — when the pill is obscuring page content
  // (e.g. the wizard's "Required to continue" footer hint or sticky
  // action buttons in the bottom-right corner), the user can shrink
  // it to a 40x40 floating chip. Persisted in localStorage so it
  // survives navigation / reload. Mar 02 2026 — user report:
  // "Due to the Jobs Status, difficult to see the Buttons at the
  // bottom right". The minimized chip still shows a red dot when
  // there are failures so the user never loses critical signal.
  const [minimized, setMinimized] = useState(() => {
    try {
      return localStorage.getItem('elv.jobsPill.minimized') === '1';
    } catch (_e) { return false; }
  });
  useEffect(() => {
    try {
      localStorage.setItem('elv.jobsPill.minimized', minimized ? '1' : '0');
    } catch (_e) { /* ignore quota / private mode */ }
  }, [minimized]);
  // Disable buttons while a retry POST is in flight to prevent
  // double-click double-fires (each retry spawns up to 5 concurrent
  // LLM calls — duplicate clicks burn real money).
  const [retryingId, setRetryingId] = useState(null);

  // 2026-02-24 — Cap pill sections + collapse queued section by
  // default. Prevents the pill from growing indefinitely during heavy
  // batch usage (which was the pre-fix experience — 15+ rows piling up).
  const [queuedOpen, setQueuedOpen] = useState(false);
  const PILL_CATEGORY_CAP = 5;
  const runningRowsFull = useMemo(
    () => rows.filter((j) => j.status === 'running'),
    [rows],
  );
  const queuedRowsFull = useMemo(
    () => rows.filter((j) => j.status === 'queued'),
    [rows],
  );
  const runningRows = useMemo(
    () => runningRowsFull.slice(0, PILL_CATEGORY_CAP),
    [runningRowsFull],
  );
  const queuedRows = useMemo(
    () => queuedRowsFull.slice(0, PILL_CATEGORY_CAP),
    [queuedRowsFull],
  );
  const runningOverflow = Math.max(0, runningRowsFull.length - runningRows.length);
  const queuedOverflow = Math.max(0, queuedRowsFull.length - queuedRows.length);

  // 2026-02-24 — Human-gate pauses. Videos sitting in stage1_complete
  // / stage2_complete are done with pipeline work but waiting for the
  // creator's approval. These get a "Review" CTA (not the destructive
  // X-cancel) so the user isn't tricked into deleting a done video.
  const awaitingReviewRows = useMemo(
    () => rows.filter((j) => j.status === 'awaiting_review').slice(0, PILL_CATEGORY_CAP),
    [rows],
  );
  const awaitingReviewOverflow = useMemo(() => {
    const total = rows.filter((j) => j.status === 'awaiting_review').length;
    return Math.max(0, total - awaitingReviewRows.length);
  }, [rows, awaitingReviewRows.length]);

  // Batch parents that finished but had ≥1 failed child. We show a
  // "Retry N failed" row in the pill for an hour after completion so
  // the creator can recover the bad blocks without re-running the
  // whole batch. After 60 min we assume the user moved on; the row
  // disappears (the parent's `result.failedBlocks` payload stays in
  // the DB indefinitely — admins can still kick a retry via the
  // POST /api/jobs/{id}/retry-failed endpoint directly).
  const retryableRows = useMemo(() => {
    const oneHourAgo = Date.now() - 60 * 60 * 1000;
    return rows.filter((j) => {
      if (j.kind !== 'batch_generation') return false;
      if (j.status !== 'succeeded') return false;
      if (dismissedFailures.has(j.id)) return false;
      const failedCount = (j.result?.failedBlocks || []).length;
      if (failedCount === 0) return false;
      const completedAt = j.completedAt || j.updatedAt;
      if (!completedAt) return true;
      return new Date(completedAt).getTime() > oneHourAgo;
    });
  }, [rows, dismissedFailures]);

  // 2026-02-24 — Hide the pill entirely while the user is inside a
  // focused-work route (learner Immersive Player, lab runner, preview
  // surfaces). The pill would otherwise float over the lab UI and
  // nag them mid-task. Jobs continue in the background; users see
  // them again the moment they navigate to a non-focused route.
  //
  // CRITICAL: this hook MUST be called before any early return in the
  // component or React blows up with error #310 ("Rendered more hooks
  // than during the previous render") — the empty-list `return null`
  // below would skip this hook on some renders and call it on others,
  // corrupting the hook order. Do NOT move it after the empty-check.
  const isFocusedFlow = useIsInFocusedFlow();

  // Auto-hide when there's nothing to show. Running jobs, failed
  // batches, AND awaiting-review pauses all keep the pill alive.
  // (Org-flag gating removed Feb 2026 — see comment at top.)
  if (runningCount === 0 && retryableRows.length === 0 && awaitingReviewRows.length === 0) return null;
  if (isFocusedFlow) return null;

  const avgProgress = (() => {
    const vals = runningRows.map((j) => Number(j.progress || 0)).filter((n) => n > 0);
    if (!vals.length) return null;
    return Math.round(vals.reduce((a, b) => a + b, 0) / vals.length);
  })();

  // Total failed-batch indicator for the collapsed header / minimized
  // badge. Surfaces "1 running · 2 failed" so the user knows there's
  // something to fix WITHOUT having to expand the pill. Mar 02 2026 —
  // user report: "shouldn't the pile give you some indication" when
  // a job has failed.
  const failedCount = retryableRows.reduce(
    (sum, j) => sum + (j.result?.failedBlocks || []).length,
    0,
  );

  const cancelOne = async (e, jobId) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await api.post(`/jobs/${jobId}/cancel`);
      toast.success('Job cancelled');
      refresh();
    } catch (err) {
      toast.error(err?.message || 'Cancel failed');
    }
  };

  // POST /api/jobs/{id}/retry-failed — backend rehydrates the
  // parent's failedBlocks list, refetches each block's current
  // content_blocks doc (in case the user edited titles/topic since
  // the bad run), and fires a fresh batch with just those IDs.
  // Returns a new parent jobId which JobsContext picks up on its
  // next poll (~3s) and the pill swaps to tracking it.
  const retryFailed = async (e, j) => {
    e.preventDefault();
    e.stopPropagation();
    if (retryingId) return;
    setRetryingId(j.id);
    try {
      const res = await api.post(`/jobs/${j.id}/retry-failed`);
      const newId = res?.data?.parentJobId;
      const retried = res?.data?.retriedCount || 0;
      const skipped = (res?.data?.deletedSkipped || []).length;
      toast.success(
        `Retrying ${retried} block${retried !== 1 ? 's' : ''}` +
        (skipped ? ` (${skipped} deleted, skipped)` : '') +
        (newId ? ` · new job ${newId.slice(0, 8)}` : ''),
      );
      // Once the user kicks off a retry, hide the old row — its
      // failures are now being addressed by the new batch.
      setDismissedFailures((prev) => new Set([...prev, j.id]));
      refresh();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === 'string'
        ? detail
        : detail?.message || 'Retry failed';
      toast.error(msg);
    } finally {
      setRetryingId(null);
    }
  };

  const dismissFailure = (e, jobId) => {
    e.preventDefault();
    e.stopPropagation();
    setDismissedFailures((prev) => new Set([...prev, jobId]));
  };

  return (
    <div
      className="fixed bottom-5 right-5 z-[60] select-none"
      data-testid="global-jobs-pill"
    >
      {minimized ? (
        // Minimized "badge" mode — a tiny 44×44 chip in the bottom-right
        // corner, frees up screen real estate for the underlying page.
        // The red dot is the failure indicator; it overrides the
        // running-spinner so an at-a-glance look tells you "something
        // needs your attention". Click anywhere on the chip to restore.
        <button
          type="button"
          onClick={() => setMinimized(false)}
          className="relative w-11 h-11 rounded-full bg-white border border-slate-200 shadow-[0_8px_24px_rgba(0,0,0,0.12)] flex items-center justify-center hover:bg-slate-50 transition-colors"
          data-testid="global-jobs-pill-restore"
          title={`${runningCount} running${failedCount > 0 ? ` · ${failedCount} failed` : ''} — click to expand`}
        >
          {runningCount > 0 ? (
            <>
              <Sparkles className="w-4 h-4 text-orange-500" />
              <Loader2 className="w-4 h-4 absolute animate-spin text-orange-300" />
            </>
          ) : (
            <AlertCircle className="w-4 h-4 text-amber-500" />
          )}
          {failedCount > 0 && (
            <span
              className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold leading-none flex items-center justify-center border-2 border-white"
              data-testid="global-jobs-pill-failed-badge"
            >
              {failedCount > 9 ? '9+' : failedCount}
            </span>
          )}
        </button>
      ) : (
      <div className="bg-white text-slate-900 rounded-xl shadow-[0_8px_24px_rgba(0,0,0,0.12)] border border-slate-200 overflow-hidden w-[320px]">
        {/* Header / collapsed pill */}
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition-colors text-left"
          data-testid="global-jobs-pill-toggle"
        >
          <div className="relative flex-shrink-0">
            {runningCount > 0 ? (
              <>
                <Sparkles className="w-4 h-4 text-orange-500" />
                <Loader2 className="w-4 h-4 absolute -top-0.5 -right-0.5 animate-spin text-orange-300" />
              </>
            ) : (
              <AlertCircle className="w-4 h-4 text-amber-500" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold leading-tight flex items-center gap-2">
              {runningCount > 0
                ? `${runningCount} ${runningCount === 1 ? 'job' : 'jobs'} running`
                : `${retryableRows.length} batch${retryableRows.length !== 1 ? 'es' : ''} with failures`}
              {/* Inline failed-count chip — only shown when there's at
                  least one running job AND some failures, since the
                  empty-running case already shows "X batches with
                  failures" in the headline above. Mar 02 2026. */}
              {runningCount > 0 && failedCount > 0 && (
                <span
                  className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-red-50 text-red-700 border border-red-200"
                  data-testid="global-jobs-pill-failed-count"
                >
                  {failedCount} failed
                </span>
              )}
            </div>
            {avgProgress !== null && runningCount > 0 && (
              <div className="text-[11px] text-slate-500 leading-tight mt-0.5">
                {avgProgress}% across all jobs
              </div>
            )}
            {runningCount === 0 && retryableRows.length > 0 && (
              <div className="text-[11px] text-amber-600 leading-tight mt-0.5">
                Click to retry failed blocks
              </div>
            )}
          </div>
          {/* Minimize-to-badge button — sits BEFORE the chevron so the
              user can fold the pill out of the way without having to
              expand it first. Mar 02 2026 — user report. */}
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setMinimized(true); }}
            className="p-1 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors flex-shrink-0"
            title="Minimize to corner badge"
            data-testid="global-jobs-pill-minimize"
            aria-label="Minimize"
          >
            <Minus className="w-3.5 h-3.5" />
          </button>
          {expanded ? (
            <ChevronDown className="w-4 h-4 text-slate-400" />
          ) : (
            <ChevronUp className="w-4 h-4 text-slate-400" />
          )}
        </button>

        {/* Overall progress bar — always visible when ≥1 job has progress */}
        {avgProgress !== null && (
          <div className="h-1 bg-slate-100">
            <div
              className="h-full bg-gradient-to-r from-orange-400 to-pink-500 transition-all duration-500"
              style={{ width: `${Math.min(100, avgProgress)}%` }}
            />
          </div>
        )}

        {/* Expanded job list */}
        {expanded && (
          <div className="border-t border-slate-100 max-h-[280px] overflow-y-auto">
            {/* Retryable batch parents — render first so the action
                is the first thing the user sees when they expand. */}            {retryableRows.map((j) => {
              const failedCount = (j.result?.failedBlocks || []).length;
              const successCount = (j.result?.succeededBlockIds || []).length;
              const isRetrying = retryingId === j.id;
              return (
                <div
                  key={`failed-${j.id}`}
                  className="px-4 py-2.5 bg-amber-50/50 border-b border-amber-100 last:border-b-0"
                  data-testid={`pill-retry-row-${j.id}`}
                >
                  <div className="flex items-start justify-between gap-2 mb-1.5">
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] font-medium text-slate-800 truncate">
                        Batch finished with errors
                      </div>
                      <div className="text-[11px] text-slate-500 mt-0.5">
                        {successCount} succeeded · <span className="text-amber-700 font-medium">{failedCount} failed</span>
                      </div>
                    </div>
                    <button
                      onClick={(e) => dismissFailure(e, j.id)}
                      className="p-0.5 rounded hover:bg-amber-100 text-slate-400 hover:text-slate-600 flex-shrink-0"
                      title="Dismiss"
                      data-testid={`pill-dismiss-${j.id}`}
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={(e) => retryFailed(e, j)}
                    disabled={isRetrying}
                    className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md bg-amber-500 hover:bg-amber-600 disabled:bg-amber-300 disabled:cursor-not-allowed text-white text-[12px] font-medium transition-colors"
                    data-testid={`pill-retry-${j.id}`}
                  >
                    {isRetrying ? (
                      <>
                        <Loader2 className="w-3 h-3 animate-spin" />
                        Starting…
                      </>
                    ) : (
                      <>
                        <RotateCcw className="w-3 h-3" />
                        Retry {failedCount} failed block{failedCount !== 1 ? 's' : ''}
                      </>
                    )}
                  </button>
                </div>
              );
            })}
            {/* 2026-02-24 — Awaiting-Review rows. Videos paused at a
                human-gate (stage1_complete / stage2_complete). These
                are NOT stuck — they're done and waiting for the
                creator's approval. We surface a violet "Review →"
                button instead of the X-cancel so users can't
                accidentally kill work they thought was finished. */}
            {awaitingReviewRows.map((j) => {
              const label = j.label || KIND_LABEL[j.kind] || j.kind || 'Awaiting review';
              const progress = Number(j.progress || 0);
              const stageMsg = j.progressMessage || 'Ready — awaiting your approval';
              const params = j.params || {};
              const deepLink = params.courseId && params.blockId
                ? `/courses/${params.courseId}/edit?focusBlock=${params.blockId}&action=review`
                : null;
              const inner = (
                <div
                  className="px-4 py-2.5 hover:bg-violet-50 transition-colors group border-l-2 border-violet-400"
                  data-testid={`pill-awaiting-review-${j.id}`}
                >
                  <div className="flex items-center justify-between gap-2 mb-1.5">
                    <div className="text-[13px] font-medium text-slate-800 truncate flex-1">
                      {label}
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      <span className="text-[11px] font-medium text-violet-700 whitespace-nowrap">
                        Review →
                      </span>
                    </div>
                  </div>
                  <div className="text-[11px] text-violet-700 mb-1">
                    {stageMsg}
                  </div>
                  <div className="h-1 bg-violet-100 rounded overflow-hidden">
                    <div
                      className="h-full bg-violet-500 transition-all duration-300"
                      style={{ width: `${Math.min(100, progress)}%` }}
                    />
                  </div>
                </div>
              );
              return deepLink ? (
                <Link
                  key={j.id}
                  to={deepLink}
                  onClick={() => setExpanded(false)}
                  className="block"
                >
                  {inner}
                </Link>
              ) : (
                <div key={j.id}>{inner}</div>
              );
            })}
            {runningRows.map((j) => {
              const label = j.label || KIND_LABEL[j.kind] || j.kind || 'Generating…';
              const progress = Number(j.progress || 0);
              const params = j.params || {};
              const deepLink = params.courseId && params.blockId
                ? `/courses/${params.courseId}/edit?focusBlock=${params.blockId}`
                : null;
              const inner = (
                <div className="px-4 py-2.5 hover:bg-slate-50 transition-colors group">
                  <div className="flex items-center justify-between gap-2 mb-1.5">
                    <div className="text-[13px] font-medium text-slate-800 truncate flex-1">
                      {label}
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      <span className="text-[10px] font-mono text-slate-500 tabular-nums">
                        {progress > 0 ? `${progress}%` : '…'}
                      </span>
                      <button
                        onClick={(e) => cancelOne(e, j.id)}
                        className="opacity-0 group-hover:opacity-100 transition-opacity p-0.5 rounded hover:bg-red-50 text-slate-400 hover:text-red-600"
                        title="Cancel job"
                        data-testid={`pill-cancel-${j.id}`}
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                  <div className="h-1 bg-slate-100 rounded overflow-hidden">
                    <div
                      className="h-full bg-orange-400 transition-all duration-300"
                      style={{ width: `${Math.min(100, progress)}%` }}
                    />
                  </div>
                </div>
              );
              return deepLink ? (
                <Link
                  key={j.id}
                  to={deepLink}
                  onClick={() => setExpanded(false)}
                  className="block"
                >
                  {inner}
                </Link>
              ) : (
                <div key={j.id}>{inner}</div>
              );
            })}
            {runningOverflow > 0 && (
              <Link
                to="/jobs"
                onClick={() => setExpanded(false)}
                className="block px-4 py-1.5 text-[11px] text-slate-500 hover:bg-slate-50 hover:text-slate-700"
                data-testid="pill-running-overflow"
              >
                + {runningOverflow} more running · View all →
              </Link>
            )}
            {(queuedRowsFull.length > 0 || awaitingReviewOverflow > 0) && (
              <>
                {awaitingReviewOverflow > 0 && (
                  <Link
                    to="/jobs?status=awaiting_review"
                    onClick={() => setExpanded(false)}
                    className="block px-4 py-1.5 text-[11px] text-violet-600 hover:bg-violet-50"
                    data-testid="pill-awaiting-review-overflow"
                  >
                    + {awaitingReviewOverflow} more awaiting review →
                  </Link>
                )}
                {queuedRowsFull.length > 0 && (
                  <>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setQueuedOpen(v => !v); }}
                      className="w-full flex items-center justify-between px-4 py-1.5 text-[11px] text-slate-600 hover:bg-slate-50 border-t"
                      data-testid="pill-queued-toggle"
                    >
                      <span>⏸ {queuedRowsFull.length} queued</span>
                      <span className="text-slate-400">{queuedOpen ? '▾' : '▸'}</span>
                    </button>
                    {queuedOpen && queuedRows.map((j) => {
                      const label = j.label || KIND_LABEL[j.kind] || j.kind || 'Queued';
                      return (
                        <div key={j.id} className="px-4 py-1.5 text-[12px] text-slate-600 truncate border-l-2 border-slate-200 bg-slate-50/60" data-testid={`pill-queued-${j.id}`}>
                          {label}
                        </div>
                      );
                    })}
                    {queuedOpen && queuedOverflow > 0 && (
                      <Link
                        to="/jobs?status=queued"
                        onClick={() => setExpanded(false)}
                        className="block px-4 py-1 text-[11px] text-slate-500 hover:bg-slate-50 hover:text-slate-700 border-l-2 border-slate-200 bg-slate-50/60"
                      >
                        + {queuedOverflow} more queued →
                      </Link>
                    )}
                  </>
                )}
              </>
            )}
            <Link
              to="/jobs"
              onClick={() => setExpanded(false)}
              className="block text-center py-2 text-[11px] text-slate-600 hover:bg-slate-50 hover:text-slate-900 border-t font-medium"
              data-testid="pill-view-history"
            >
              View all jobs history →
            </Link>
          </div>
        )}
      </div>
      )}
    </div>
  );
}
